import type { JourneyPath } from '../types/journey';

export const OFFLINE_CACHE = 'mynewstart-offline-v1';
const OFFLINE_KEY = 'mns-offline-courses-v1';
const SAVED_COURSES_KEY = 'mns-saved-courses-v1';

interface OfflineFile {
  courses: Record<string, { savedAt: string; path: JourneyPath }>;
}

function storage(): Storage | null {
  try {
    return localStorage;
  } catch {
    return null;
  }
}

function readFile(): OfflineFile {
  const store = storage();
  if (!store) return { courses: {} };
  try {
    const parsed = JSON.parse(store.getItem(OFFLINE_KEY) || '{}') as Partial<OfflineFile>;
    return { courses: parsed.courses && typeof parsed.courses === 'object' ? parsed.courses : {} };
  } catch {
    return { courses: {} };
  }
}

export function readOfflineCourse(slug: string): JourneyPath | null {
  const course = readFile().courses[slug];
  if (!course?.path || course.path.slug !== slug) return null;
  return course.path;
}

export function writeOfflineCourse(path: JourneyPath): boolean {
  const store = storage();
  if (!store) return false;
  try {
    const file = readFile();
    file.courses[path.slug] = { savedAt: new Date().toISOString(), path };
    store.setItem(OFFLINE_KEY, JSON.stringify(file));
    return true;
  } catch {
    return false;
  }
}

export function offlineCourseSlugs(): string[] {
  return Object.keys(readFile().courses);
}

function readSavedSlugs(): string[] {
  const store = storage();
  if (!store) return [];
  try {
    const parsed = JSON.parse(store.getItem(SAVED_COURSES_KEY) || '[]');
    return Array.isArray(parsed) ? parsed.map(String) : [];
  } catch {
    return [];
  }
}

export function savedCourseSlugs(): string[] {
  return readSavedSlugs();
}

export function isCourseSaved(slug: string): boolean {
  return readSavedSlugs().includes(slug);
}

export function toggleSavedCourse(slug: string): boolean {
  const store = storage();
  if (!store) return false;
  const current = readSavedSlugs();
  const next = current.includes(slug) ? current.filter((item) => item !== slug) : [...current, slug];
  store.setItem(SAVED_COURSES_KEY, JSON.stringify(next));
  return next.includes(slug);
}

export function coursePageUrls(origin: string, path: JourneyPath): string[] {
  const root = origin.replace(/\/$/, '');
  const urls = [`${root}/`, `${root}/courses/${path.slug}`, `${root}/home`, `${root}/review`];
  for (const module of path.modules || []) {
    for (const lesson of module.lessons || []) {
      urls.push(`${root}/courses/${path.slug}/topics/${lesson.slug}`);
      urls.push(`${root}/courses/${path.slug}/topics/${lesson.slug}/learn`);
    }
  }
  return urls;
}

/** Cache the course pages and the files already on this screen. One failure does not cancel the rest. */
export async function cacheCoursePages(urls: string[]): Promise<number> {
  if (typeof caches === 'undefined') return 0;
  const extras = typeof document === 'undefined'
    ? []
    : [...document.querySelectorAll('script[src], link[rel="stylesheet"]')]
        .map((node) => ('src' in node ? (node as HTMLScriptElement).src : (node as HTMLLinkElement).href))
        .filter((url) => url.startsWith(location.origin));
  const cache = await caches.open(OFFLINE_CACHE);
  let saved = 0;
  for (const url of [...urls, ...extras]) {
    try {
      await cache.add(url);
      saved += 1;
    } catch {
      // A missing file stays online. The course text is already on the device.
    }
  }
  return saved;
}
